import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  fail,
  handle,
  idOf,
  nextDocNo,
  parse,
  requireRole,
  roleCodesOf,
  statusCodeOf,
  stockMovement,
  txLog,
  workflowRunner
} from './c07.shared'
import { ngReport } from './inventory.handler'
import { notify, wmsRoute } from './notification.shared'

/**
 * Endpoint custom C12 Pemusnahan RM — `/api/v1/wms/destruction-rm/*` (WF-DSP-02, entitas `rm_destruction`):
 * - baca: `queue` (rm_ng_item di Zona Not Good yang belum punya BA), `documents`, `document?id=`, `my-reports` (DSR-05).
 * - DSR-02: `create` / `assign-move` / `complete` (Admin), `approve` / `reject` (Supervisor).
 * - DSR-05: `report` (Operator, memakai aturan laporan temuan C16) + `my-reports`.
 * Pemindahan fisik ke Area Pemusnahan dikerjakan DSR-03 (C14): ia menaikkan `rm_ng_item.item_status` ke `AT_DESTRUCTION`.
 */

const flow = workflowRunner('WF-DSP-02', 'rm_destruction')
const ADMIN = 'admin_wh_rm'
const SPV = 'wh_rm_supervisor'
const OPERATOR = 'operator_wh_rm'
export const ST = {
  queue: 'MOVED',
  scheduled: 'SCHEDULED_DESTROY',
  moving: 'MOVING_TO_DESTROY',
  atArea: 'AT_DESTRUCTION',
  destroyed: 'DESTROYED'
} as const

type NgLog = { at: string; ev: string; by: string | null }
const num = (value: unknown) => Number(value ?? 0)
const iso = () => new Date().toISOString()
const stamp = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 16).replace('T', ' ')
const todayWib = () => new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10)
const userName = (id: unknown) => {
  const user = findRow('directus_users', idOf(id))
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ')
}
const logOf = (item: LocalRow) => ((item.log as NgLog[] | undefined) ?? []).slice()
const withLog = (item: LocalRow, ev: string) => [...logOf(item), { at: stamp(), ev, by: currentUserId() }]

const docOf = (id: unknown) => {
  const doc = findRow('rm_destruction', String(id ?? ''))
  if (!doc) fail('Berita acara tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return doc as LocalRow
}
const itemsOf = (doc: LocalRow) =>
  readTable('rm_ng_item')
    .filter(item => idOf(item.destruction_id) === doc.id)
    .sort((a, b) => String(a.ngz_no ?? a.id).localeCompare(String(b.ngz_no ?? b.id)))

/* ============================================================ baris barang NG */

function itemRow(item: LocalRow) {
  const material = findRow('mt_material', idOf(item.material_id))
  const reasonIds = (item.reason_ids as string[] | undefined) ?? (item.reason_id ? [String(item.reason_id)] : [])
  const session = String(item.source_ref ?? '')
  return {
    id: String(item.id),
    ngz_no: String(item.ngz_no ?? ''),
    sticker_ngq: String(item.sticker_ngq ?? ''),
    material_code: String(material?.code ?? ''),
    material_name: String(material?.name ?? ''),
    batch_no: String(item.batch_no ?? ''),
    qty: num(item.qty),
    unit_name: String(item.unit_name ?? ''),
    reasons: reasonIds.map(id => String(findRow('mt_reject_type', id)?.name ?? '')).filter(Boolean),
    source: String(item.source),
    source_ref: session,
    status: String(item.item_status),
    operator: userName(item.destroy_operator_id),
    task_no: (item.destroy_task_no as string | null) ?? null
  }
}

export function queue() {
  const rows = readTable('rm_ng_item')
    .filter(item => item.item_status === ST.queue && !item.destruction_id)
    .map(itemRow)
    .sort((a, b) => a.ngz_no.localeCompare(b.ngz_no))
  return { rows, total_qty: rows.reduce((sum, row) => sum + row.qty, 0) }
}

/* ============================================================ dokumen */

const statusName = (doc: LocalRow) => String(findRow('wml_status', idOf(doc.status_id))?.name ?? '')

function summary(doc: LocalRow) {
  const rows = itemsOf(doc).map(itemRow)
  const code = statusCodeOf(doc)
  return {
    id: String(doc.id),
    bap_no: String(doc.bap_no),
    status: code,
    status_name: statusName(doc),
    ready: code === 'DSP_RM_ASSIGNED' && rows.length > 0 && rows.every(row => row.status === ST.atArea),
    destruct_date: String(doc.destruct_date ?? ''),
    destruct_time: String(doc.destruct_time ?? ''),
    place: String(doc.place ?? ''),
    created_by: userName(doc.user_created),
    approver: userName(doc.approver_id),
    approved_at: (doc.approved_at as string | null) ?? null,
    reject_reason: (doc.reject_reason as string | null) ?? null,
    done_at: (doc.done_at as string | null) ?? null,
    done_by: userName(doc.done_by_id),
    line_count: rows.length,
    unit_total: rows.reduce((sum, row) => sum + row.qty, 0),
    material_count: new Set(rows.map(row => row.material_code)).size,
    sources: [...new Set(rows.map(row => (row.source === 'OPNAME' ? row.source_ref : 'Temuan harian')))]
  }
}

export const documents = () =>
  readTable('rm_destruction')
    .map(summary)
    .sort((a, b) => b.bap_no.localeCompare(a.bap_no))

const stepLabel: Record<string, string> = {
  ACT_DSP_RM_SEND: 'Berita acara dibuat dan diajukan ke Supervisor',
  ACT_DSP_RM_APP: 'Jadwal pemusnahan disetujui',
  ACT_DSP_RM_REJECT: 'Jadwal ditolak',
  ACT_DSP_RM_ASSIGN_TASK: 'Tugas pemindahan ke Area Pemusnahan diterbitkan',
  ACT_DSP_RM_COMPLETE: 'Pemusnahan selesai dilaksanakan'
}

export function document(params: { id?: string } | undefined) {
  const doc = findRow('rm_destruction', String(params?.id ?? ''))
  if (!doc) return null
  const submissionId = idOf(doc.submission_id)
  const log = readTable('wxl_submission_log')
    .filter(row => idOf(row.submission_id) === submissionId)
    .sort((a, b) => String(a.date_created).localeCompare(String(b.date_created)))
    .map(row => ({
      at: String(row.date_created),
      by: userName(row.user_created),
      event: stepLabel[String(findRow('wml_action', idOf(row.action_id))?.name ?? '')] ?? 'Perubahan status',
      reason: (row.reason as string | null) ?? null
    }))
  const items = itemsOf(doc as LocalRow).map(itemRow)
  const tasks = new Map<string, { task_no: string; operator: string; items: number; qty: number }>()
  items.forEach(row => {
    if (!row.task_no) return
    const task = tasks.get(row.task_no) ?? { task_no: row.task_no, operator: row.operator, items: 0, qty: 0 }
    task.items += 1
    task.qty += row.qty
    tasks.set(row.task_no, task)
  })
  return { ...summary(doc as LocalRow), items, tasks: [...tasks.values()], log }
}

/** Buat BA: satu dokumen untuk barang NG lintas sesi opname / temuan harian; terbit "Menunggu Approval". */
export function create(body: { item_ids?: string[]; date?: string; time?: string; place?: string }) {
  requireRole('Pembuatan BA hanya untuk Admin WH RM.', ADMIN)
  const ids = [...new Set(body.item_ids ?? [])]
  if (!ids.length) fail('Belum ada baris yang dipilih.')
  const date = String(body.date ?? '').trim()
  if (!date) fail('Tanggal pemusnahan harus diisi.')
  if (date < todayWib()) fail('Tanggalnya sudah lewat. Pilih hari ini atau sesudahnya.')
  const items = ids.map(id => findRow('rm_ng_item', id) as LocalRow | undefined)
  if (items.some(item => !item || item.item_status !== ST.queue || item.destruction_id))
    fail('Sebagian barang sudah dijadwalkan atau berubah — muat ulang antreannya.', 'ERR_STATE_CONFLICT', 409)
  const id = newId()
  const { statusId, submissionId } = flow.start(id)
  const bapNo = nextDocNo('BAP-RM', 'rm_destruction', 'bap_no')
  const doc = insertRow('rm_destruction', {
    ...auditFieldsForCreate(),
    id,
    bap_no: bapNo,
    destruct_date: date,
    destruct_time: String(body.time ?? '').trim() || '09:00',
    place: String(body.place ?? '').trim() || 'Area Pemusnahan — Plant Kudus',
    status_id: statusId,
    submission_id: submissionId,
    approver_id: null,
    approved_at: null,
    reject_reason: null,
    done_at: null,
    done_by_id: null,
    is_destroyed: false,
    destroyed_at: null
  })
  items.forEach(item =>
    updateRow('rm_ng_item', String(item?.id), {
      destruction_id: id,
      item_status: ST.scheduled,
      log: withLog(item as LocalRow, `Dijadwalkan musnah di ${bapNo} (${date})`),
      ...auditFieldsForUpdate()
    })
  )
  flow.run(doc, 'ACT_DSP_RM_SEND', ['ACT_DSP_RM_ADMIN_RM'])
  txLog('RM Destruction', 'DSR-02', 'create', bapNo, `${items.length} baris dijadwalkan ${date}`)
  return { id, bap_no: bapNo, lines: items.length }
}

export function approve(body: { id?: string }) {
  requireRole('Approval jadwal hanya untuk Supervisor WH RM.', SPV)
  const doc = docOf(body.id)
  if (idOf(doc.user_created) === currentUserId())
    fail('Pembuat dokumen tidak boleh menyetujuinya sendiri.', 'ERR_FORBIDDEN', 403)
  flow.run(doc, 'ACT_DSP_RM_APP', ['ACT_DSP_RM_SPV_RM'])
  updateRow('rm_destruction', String(doc.id), {
    approver_id: currentUserId(),
    approved_at: iso(),
    ...auditFieldsForUpdate()
  })
  txLog('RM Destruction', 'DSR-02', 'approve', String(doc.bap_no), 'Jadwal pemusnahan disetujui')
  return { id: doc.id }
}

export function reject(body: { id?: string; reason?: string }) {
  requireRole('Penolakan jadwal hanya untuk Supervisor WH RM.', SPV)
  const doc = docOf(body.id)
  const reason = String(body.reason ?? '').trim()
  if (reason.length < 3) fail('Alasannya diisi dulu.')
  flow.run(doc, 'ACT_DSP_RM_REJECT', ['ACT_DSP_RM_SPV_RM'], {}, reason)
  updateRow('rm_destruction', String(doc.id), { reject_reason: reason, ...auditFieldsForUpdate() })
  /** Barangnya tetap di Zona Not Good — kembali ke antrean, lepas dari dokumen. */
  itemsOf(doc).forEach(item =>
    updateRow('rm_ng_item', String(item.id), {
      destruction_id: null,
      item_status: ST.queue,
      log: withLog(item, `Jadwal ${String(doc.bap_no)} ditolak — kembali ke antrean Area Not Good`),
      ...auditFieldsForUpdate()
    })
  )
  txLog('RM Destruction', 'DSR-02', 'reject', String(doc.bap_no), `Jadwal ditolak: ${reason}`)
  return { id: doc.id }
}

export function assignMove(body: { id?: string; assignments?: { operator_id: string; item_ids: string[] }[] }) {
  requireRole('Penerbitan tugas pindah hanya untuk Admin WH RM.', ADMIN)
  const doc = docOf(body.id)
  if (statusCodeOf(doc) !== 'DSP_RM_APPROVED')
    fail('Tugas pindah terbit setelah jadwal disetujui.', 'ERR_WORKFLOW_STATE_MISMATCH', 422)
  const assignments = body.assignments ?? []
  if (!assignments.length) fail('Pilih paling tidak satu petugas beserta barang yang dibawanya.')
  const operators = assignments.map(row => row.operator_id)
  if (new Set(operators).size !== operators.length) fail('Satu petugas hanya boleh muncul sekali.')
  operators.forEach(id => {
    if (!roleCodesOf(id).includes(OPERATOR)) fail('Petugas harus Operator WH RM.')
  })
  const all = itemsOf(doc)
  const given = assignments.flatMap(row => row.item_ids)
  if (new Set(given).size !== given.length) fail('Satu baris tidak boleh dibagi ke dua petugas.')
  if (all.some(item => !given.includes(String(item.id)))) fail('Masih ada baris yang belum dibagikan ke petugas.')
  assignments.forEach(row => {
    const taskNo = nextDocNo('PMR', 'rm_ng_item', 'destroy_task_no')
    row.item_ids.forEach(itemId => {
      const item = all.find(entry => String(entry.id) === itemId)
      if (!item) fail('Baris tidak ada di dokumen ini.', 'ERR_NOT_FOUND', 404)
      updateRow('rm_ng_item', itemId, {
        item_status: ST.moving,
        destroy_operator_id: row.operator_id,
        destroy_task_no: taskNo,
        log: withLog(
          item as LocalRow,
          `Ditugaskan ke ${userName(row.operator_id)} (${taskNo}) — pindah ke Area Pemusnahan`
        ),
        ...auditFieldsForUpdate()
      })
    })
  })
  flow.run(doc, 'ACT_DSP_RM_ASSIGN_TASK', ['ACT_DSP_RM_ADMIN_RM'])
  txLog(
    'RM Destruction',
    'DSR-02',
    'assign-move',
    String(doc.bap_no),
    `Tugas pindah untuk ${assignments.length} petugas`
  )
  assignments.forEach(row =>
    notify({
      to: { userIds: [row.operator_id] },
      type: 'DESTRUCTION_ASSIGNED',
      title: `Tugas pemusnahan RM ${doc.bap_no}`,
      body: `Bawa ${row.item_ids.length} barang NG ke Area Pemusnahan — buka Berita Acara Pemusnahan.`,
      link: wmsRoute('destruction-rm/berita-acara-pemusnahan', { id: String(doc.id) }),
      ref: { type: 'rm_destruction', id: String(doc.id) }
    })
  )
  return { id: doc.id, tasks: assignments.length }
}

export function complete(body: { id?: string }) {
  requireRole('Hanya Admin WH RM yang menandai pemusnahan selesai.', ADMIN)
  const doc = docOf(body.id)
  const items = itemsOf(doc)
  if (statusCodeOf(doc) === 'DSP_RM_ASSIGNED' && items.some(item => item.item_status !== ST.atArea))
    fail('Barangnya belum semua sampai di Area Pemusnahan.', 'ERR_STATE_CONFLICT', 409)
  flow.run(doc, 'ACT_DSP_RM_COMPLETE', ['ACT_DSP_RM_ADMIN_RM'])
  const now = iso()
  items.forEach(item => {
    const unit = findRow('rm_unit', idOf(item.unit_id)) as LocalRow | undefined
    if (unit) {
      const perBig = Math.max(1, num(unit.small_per_big_snapshot))
      const left = Math.max(0, num(unit.qty_small) - num(item.qty) * perBig)
      stockMovement('RM_DESTROY', unit, num(item.qty) * perBig, unit.rack_bin_id, null, String(doc.bap_no))
      updateRow('rm_unit', String(unit.id), {
        qty_small: left,
        ...(left === 0 ? { unit_status: 'DESTROYED' } : {}),
        ...auditFieldsForUpdate()
      })
    }
    updateRow('rm_ng_item', String(item.id), {
      item_status: ST.destroyed,
      log: withLog(item, `Pemusnahan dilaksanakan — stok keluar permanen (${String(doc.bap_no)})`),
      ...auditFieldsForUpdate()
    })
  })
  updateRow('rm_destruction', String(doc.id), { done_at: now, done_by_id: currentUserId(), ...auditFieldsForUpdate() })
  txLog('RM Destruction', 'DSR-02', 'complete', String(doc.bap_no), `${items.length} baris dimusnahkan`)
  return { id: doc.id }
}

/* ============================================================ DSR-05 */

export function myReports() {
  const me = currentUserId()
  const mine = readTable('rm_ng_item')
    .filter(item => item.source === 'TEMUAN' && idOf(item.reported_by) === me)
    .sort((a, b) => String(b.date_created).localeCompare(String(a.date_created)))
  return mine.map(item => ({
    ...itemRow(item),
    finding_no: String(item.finding_no ?? ''),
    note: (item.note as string | null) ?? null,
    reported_at: String(item.date_created),
    all_reasons: itemRow(item).reasons
  }))
}

export const report = ngReport

export function registerDestructionRmHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/destruction-rm'
  const get = (path: string, run: (params: Record<string, unknown> | undefined) => unknown) =>
    mock
      .onGet(`${base}/${path}`)
      .reply(config => handle(() => run(config.params as Record<string, unknown> | undefined)))
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  get('queue', () => queue())
  get('documents', () => documents())
  get('document', params => document(params as { id?: string }))
  get('my-reports', () => myReports())
  post('create', create)
  post('approve', approve)
  post('reject', reject)
  post('assign-move', assignMove)
  post('complete', complete)
  post('report', report)
}
